import { useEffect, useState } from 'react';
import { Redirect, Route, Switch, useLocation, useRoute } from 'wouter';
import { api, signInAdvice, type Bucket, type Health, type Stats } from './lib/api';
import { useResource, useNow } from './lib/use-resource';
import { Header } from './components/Header';
import { Confirm, type ConfirmRequest } from './components/Confirm';
import { Desk } from './screens/Desk';
import { Lifeline } from './screens/Lifeline';
import { Classes } from './screens/Classes';
import { HealthBoard } from './screens/Health';
import { DoneToday } from './screens/DoneToday';
import { Finance } from './screens/Finance';
import { FinanceAudit } from './screens/FinanceAudit';
import { Knowledge } from './screens/Knowledge';
import { Notices } from './screens/Notices';

const NARROW = '(max-width: 900px)';

export function App() {
  const [confirm, setConfirm] = useState<ConfirmRequest | null>(null);
  const narrow = useNarrow();
  const now = useNow(30_000);

  const stats = useResource<Stats>(() => api.stats(), { pollMs: 20_000 });
  const health = useResource<Health>(() => api.health(), { pollMs: 60_000 });

  // The whole app is behind Cloudflare Access, so the first failed call is how
  // a lapsed session announces itself. Saying that plainly beats four empty
  // panels and a spinner.
  if (stats.error && !stats.data) return <Locked reason={stats.error.message} next={stats.error.next} />;

  const refresh = () => { stats.refetch(); health.refetch(); };
  // Who Access says is signed in. Used only to say "you" instead of printing
  // the operator's own address back at him.
  const me = health.data?.you?.email ?? null;
  // #217: Try now is the operator's; the desk refuses anyone else, the console does not offer it.
  const operator = health.data?.you?.role === 'operator';

  return (
    <div className="shell">
      <Header stats={stats} health={health} onConfirm={setConfirm} onChanged={refresh} />

      <div className="shell-body">
        <Switch>
          <Route path="/lifeline"><Lifeline /></Route>
          <Route path="/classes"><Classes onConfirm={setConfirm} /></Route>
          {/* The screen was Trust until Phase 8.4; old links still land. */}
          <Route path="/trust"><Redirect to="/classes" /></Route>
          <Route path="/health"><HealthBoard onConfirm={setConfirm} /></Route>
          <Route path="/done"><DoneToday me={me} /></Route>
          <Route path="/finance"><Finance onConfirm={setConfirm} /></Route>
          <Route path="/finance/audit"><FinanceAudit onConfirm={setConfirm} /></Route>
          <Route path="/knowledge"><Knowledge onConfirm={setConfirm} operator={operator} /></Route>
          <Route path="/knowledge/:rest*"><Knowledge onConfirm={setConfirm} operator={operator} /></Route>
          {/* spec 014: a template opens at /knowledge/templates/:id — two segments, which :rest* does not match. */}
          <Route path="/knowledge/templates/:id"><Knowledge onConfirm={setConfirm} operator={operator} /></Route>
          <Route path="/notices"><Notices onConfirm={setConfirm} operator={operator} /></Route>
          <Route>
            <DeskRoutes
              stats={stats} onConfirm={setConfirm} onChanged={refresh}
              narrow={narrow} now={now} me={me} operator={operator}
            />
          </Route>
        </Switch>
      </div>

      <Confirm request={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

/**
 * Three paths onto one screen: the default queue, a named bucket, and a single
 * card. `/cards/:id` exists because that is the link every Slack post carries.
 */
function DeskRoutes(props: {
  stats: ReturnType<typeof useResource<Stats>>;
  onConfirm: (r: ConfirmRequest) => void;
  onChanged: () => void;
  narrow: boolean;
  now: number;
  me: string | null;
  operator: boolean;
}) {
  // Three shapes, and the third is why the bucket used to vanish.
  //   /cards/:id            the address Slack posts, no bucket named
  //   /b/:bucket            a bucket with nothing open
  //   /b/:bucket/cards/:id  a card open INSIDE a bucket
  // Without the third, opening a card from a bucket dropped the bucket and the
  // queue reverted to Needs you — see cardHref in Rail.tsx.
  const [, params] = useRoute('/cards/:id');
  const [, bucketParams] = useRoute('/b/:bucket');
  const [, bothParams] = useRoute('/b/:bucket/cards/:id');
  const rawId = bothParams?.id ?? params?.id;
  const cardId = rawId ? Number(rawId) : null;
  const bucket = ((bothParams?.bucket ?? bucketParams?.bucket) as Bucket) ?? 'needs_you';

  return (
    <Desk
      bucket={bucket}
      cardId={Number.isFinite(cardId) ? cardId : null}
      stats={props.stats}
      onConfirm={props.onConfirm}
      onChanged={props.onChanged}
      me={props.me}
      operator={props.operator}
      narrow={props.narrow}
      now={props.now}
    />
  );
}

function Locked({ reason, next }: { reason: string; next: string | null }) {
  const [, setLocation] = useLocation();
  // "Your session lapsed" and "you are at an address with no sign-in in front
  // of it" are the same 401 to this app, and the useful advice is opposite:
  // one is fixed by reloading and the other never is.
  const wrongHost = signInAdvice(window.location.hostname);

  return (
    <div className="boot">
      <div className="boot-card">
        <div className="boot-eyebrow">Click2Call Desk</div>
        <h1>{wrongHost ? 'This is the wrong address.' : 'The desk would not answer.'}</h1>
        <p>{wrongHost ? wrongHost.message : next ?? 'Reload the page to renew the Cloudflare Access session.'}</p>
        <div className="boot-detail">{reason}</div>
        <div className="boot-actions">
          {wrongHost ? (
            <a className="btn-primary btn-link" href={wrongHost.url}>Go to the console</a>
          ) : (
            <button type="button" className="btn-primary" onClick={() => window.location.reload()}>
              Reload
            </button>
          )}
          <button type="button" className="btn-ghost" onClick={() => setLocation('/')}>
            Back to the queue
          </button>
        </div>
      </div>
    </div>
  );
}

function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(() => window.matchMedia(NARROW).matches);
  useEffect(() => {
    const mq = window.matchMedia(NARROW);
    const on = () => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return narrow;
}
